import { Fragment, useState } from 'react';
import DOMPurify from 'dompurify';
import clsx from 'clsx';
import { Editor } from '@tinymce/tinymce-react';
import { useSnackbar } from 'notistack';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useCreateCMSFAQItemMutation,
  useDeleteCMSFAQItemMutation,
  useGetCMSFAQAdminPageQuery,
  useUpdateCMSFAQItemMutation,
} from '@/services/CmsFAQService';
import { ErrorApiResponse } from '@/types/api.types';
import { CMSFAQItem, languageOptions } from '@/types/cms.types';
import CMSFAQTranslationWorkflowSection from './CMSFAQTranslationWorkflowSection';
import NumberInput from '@/components/inputControls/NumberInput';

interface FAQEditorState {
  id?: string;
  source_language: string;
  title: string;
  content: string;
  display_order: number;
}

interface SourceFieldEditState {
  title: boolean;
  content: boolean;
}

const EMPTY_EDITOR: FAQEditorState = {
  source_language: 'en',
  title: '',
  content: '',
  display_order: 1,
};

const stripHtmlContent = (value: string) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toEditorState = (item: CMSFAQItem): FAQEditorState => ({
  id: item.id,
  source_language: item.source_language,
  title: item.title,
  content: item.content,
  display_order: item.display_order,
});

const getLanguageLabel = (languageCode: string) =>
  languageOptions.find((option) => option.value === languageCode)?.label ||
  languageCode.toUpperCase();

const getErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as ErrorApiResponse & {
    data?: {
      message?: string;
      details?: string;
    };
  };

  return (
    apiError?.data?.details ||
    apiError?.data?.message ||
    apiError?.message ||
    fallback
  );
};

const FAQ = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [activeItemId, setActiveItemId] = useState<string | 'new' | null>(null);
  const [editorState, setEditorState] = useState<FAQEditorState>(EMPTY_EDITOR);
  const [fieldEditState, setFieldEditState] = useState<SourceFieldEditState>({
    title: false,
    content: false,
  });
  const [errors, setErrors] = useState<{
    title?: string;
    content?: string;
    display_order?: string;
  }>({});

  const {
    data: faqPageResponse,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetCMSFAQAdminPageQuery();
  const [createFAQItem, { isLoading: isCreating }] =
    useCreateCMSFAQItemMutation();
  const [updateFAQItem, { isLoading: isUpdating }] =
    useUpdateCMSFAQItemMutation();
  const [deleteFAQItem, { isLoading: isDeleting }] =
    useDeleteCMSFAQItemMutation();

  const items = [...(faqPageResponse?.data?.items || [])].sort(
    (left, right) => left.display_order - right.display_order
  );
  const hasReadPermission = hasPermission('cms_management_read');
  const hasCreatePermission = hasPermission('cms_management_create');
  const hasUpdatePermission = hasPermission('cms_management_update');
  const hasDeletePermission = hasPermission('cms_management_delete');
  const isMutationRunning = isCreating || isUpdating || isDeleting;

  const resetEditor = () => {
    setActiveItemId(null);
    setEditorState(EMPTY_EDITOR);
    setFieldEditState({
      title: false,
      content: false,
    });
    setErrors({});
  };

  const validateEditor = () => {
    const nextErrors: {
      title?: string;
      content?: string;
      display_order?: string;
    } = {};

    if (!editorState.title.trim()) {
      nextErrors.title = 'Title is required';
    }

    if (!stripHtmlContent(editorState.content)) {
      nextErrors.content = 'Content is required';
    }

    if (editorState.display_order < 1) {
      nextErrors.display_order = 'Display order must be at least 1';
    } else if (
      items.some(
        (item) =>
          item.display_order === editorState.display_order &&
          item.id !== editorState.id
      )
    ) {
      nextErrors.display_order =
        'Display order must be unique within the FAQ page';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      enqueueSnackbar('Please fix the highlighted FAQ item fields', {
        variant: 'error',
      });
      return false;
    }

    return true;
  };

  const handleCreateClick = () => {
    const nextDisplayOrder =
      items.reduce(
        (highestDisplayOrder, item) =>
          Math.max(highestDisplayOrder, item.display_order || 0),
        0
      ) + 1;

    setActiveItemId('new');
    setEditorState({
      ...EMPTY_EDITOR,
      display_order: nextDisplayOrder,
    });
    setFieldEditState({
      title: false,
      content: false,
    });
    setErrors({});
  };

  const handleEditClick = (item: CMSFAQItem) => {
    setActiveItemId(item.id);
    setEditorState(toEditorState(item));
    setFieldEditState({
      title: false,
      content: false,
    });
    setErrors({});
  };

  const toggleFieldEdit = (field: keyof SourceFieldEditState) => {
    setFieldEditState((previous) => ({
      ...previous,
      [field]: !previous[field],
    }));
  };

  const handleSave = async () => {
    if (!validateEditor()) return;

    try {
      if (activeItemId === 'new') {
        await createFAQItem({
          source_language: editorState.source_language,
          title: editorState.title.trim(),
          content: editorState.content,
          display_order: editorState.display_order,
        }).unwrap();

        enqueueSnackbar(
          'FAQ item created successfully. You can now manage translations.',
          {
            variant: 'success',
          }
        );
      } else if (editorState.id) {
        await updateFAQItem({
          id: editorState.id,
          source_language: editorState.source_language,
          title: editorState.title.trim(),
          content: editorState.content,
          display_order: editorState.display_order,
        }).unwrap();

        enqueueSnackbar('Source FAQ content saved successfully', {
          variant: 'success',
        });
      }

      resetEditor();
      await refetch();
    } catch (saveError) {
      enqueueSnackbar(
        getErrorMessage(saveError, 'Failed to save CMS FAQ item'),
        {
          variant: 'error',
        }
      );
    }
  };

  const handleDelete = async (item: CMSFAQItem) => {
    try {
      await deleteFAQItem(item.id).unwrap();
      enqueueSnackbar('FAQ item deleted successfully', {
        variant: 'success',
      });

      if (activeItemId === item.id) {
        resetEditor();
      }

      await refetch();
    } catch (deleteError) {
      enqueueSnackbar(
        getErrorMessage(deleteError, 'Failed to delete CMS FAQ item'),
        {
          variant: 'error',
        }
      );
    }
  };

  const renderEditor = (isNewItem: boolean) => (
    <div className="space-y-6">
      {isNewItem && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900 dark:text-blue-200 contrast:border-blue-800 contrast:bg-blue-900 contrast:text-white">
          Save the FAQ item once to unlock the draft, publish, and rollback
          translation workflow.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label
            className="form-label required"
            htmlFor="cms-faq-source-language"
          >
            Source Language
          </label>
          <select
            id="cms-faq-source-language"
            value={editorState.source_language}
            onChange={(event) =>
              setEditorState((previous) => ({
                ...previous,
                source_language: event.target.value,
              }))
            }
            className="select select-bordered w-full"
            disabled={isMutationRunning}
          >
            {languageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            className="form-label required"
            htmlFor="cms-faq-display-order"
          >
            Display Order
          </label>
          <NumberInput
            id="cms-faq-display-order"
            value={editorState.display_order}
            onChange={(event) => {
              setEditorState((previous) => ({
                ...previous,
                display_order: parseInt(event.target.value, 10) || 1,
              }));
              if (errors.display_order) {
                setErrors((previous) => ({
                  ...previous,
                  display_order: undefined,
                }));
              }
            }}
            className={clsx(
              'input input-sm h-10 w-full',
              errors.display_order && 'input-error'
            )}
            min={1}
            placeholder="Enter display order"
            autoComplete="off"
            aria-label="Display order"
            disabled={isMutationRunning}
          />
          {errors.display_order && (
            <span className="text-red-500 text-sm">{errors.display_order}</span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
          <div className="space-y-3">
            <div>
              <p className="form-label required">Title</p>
              {fieldEditState.title ? (
                <input
                  id="cms-faq-title"
                  type="text"
                  value={editorState.title}
                  onChange={(event) => {
                    setEditorState((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }));
                    if (errors.title) {
                      setErrors((previous) => ({
                        ...previous,
                        title: undefined,
                      }));
                    }
                  }}
                  className={clsx(
                    'input input-bordered w-full',
                    errors.title && 'input-error'
                  )}
                  placeholder="Enter FAQ title"
                  aria-label="FAQ title"
                  autoComplete="off"
                  maxLength={100}
                  disabled={isMutationRunning}
                />
              ) : (
                <div className="min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-800 dark:border-coal-100 dark:bg-coal-600 dark:text-gray-900 contrast:border-coal-100 contrast:bg-coal-600 contrast:text-white">
                  {editorState.title || 'No title added yet'}
                </div>
              )}
            </div>
            {errors.title && (
              <span className="text-red-500 text-sm">{errors.title}</span>
            )}
            <button
              className="btn btn-sm btn-light"
              onClick={() => toggleFieldEdit('title')}
              disabled={isMutationRunning}
            >
              <KeenIcon
                icon={fieldEditState.title ? 'cross' : 'pencil'}
                className="mr-2"
              />
              {fieldEditState.title ? 'Close Title' : 'Edit Title'}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
          <div className="space-y-3">
            <div>
              <p className="form-label required">Description</p>
              {fieldEditState.content ? (
                <Editor
                  tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                  value={editorState.content}
                  init={{ ...tinymceInit, height: 260 }}
                  onEditorChange={(content: string) => {
                    setEditorState((previous) => ({
                      ...previous,
                      content,
                    }));
                    if (errors.content) {
                      setErrors((previous) => ({
                        ...previous,
                        content: undefined,
                      }));
                    }
                  }}
                  textareaName="cms-faq-content"
                  disabled={isMutationRunning}
                />
              ) : (
                <div
                  className="min-h-[140px] rounded-lg border border-gray-200 bg-white px-4 py-4 text-gray-700 dark:border-coal-100 dark:bg-coal-600 dark:text-gray-900 contrast:border-coal-100 contrast:bg-coal-600 contrast:text-white prose prose-sm dark:prose-headings:text-white dark:prose-strong:text-white dark:prose-a:text-blue-300 contrast:prose-headings:text-white contrast:prose-strong:text-white contrast:prose-a:text-blue-300 max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      editorState.content || '<p>No description added yet</p>'
                    ),
                  }}
                />
              )}
            </div>
            {errors.content && (
              <span className="text-red-500 text-sm">{errors.content}</span>
            )}
            <button
              className="btn btn-sm btn-light"
              onClick={() => toggleFieldEdit('content')}
              disabled={isMutationRunning}
            >
              <KeenIcon
                icon={fieldEditState.content ? 'cross' : 'pencil'}
                className="mr-2"
              />
              {fieldEditState.content
                ? 'Close Description'
                : 'Edit Description'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
        <button
          className="btn btn-light"
          onClick={resetEditor}
          disabled={isMutationRunning}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isMutationRunning}
        >
          {isMutationRunning ? (
            <span className="flex items-center">
              <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              {isNewItem ? 'Creating...' : 'Saving...'}
            </span>
          ) : (
            <span className="flex items-center">
              <KeenIcon icon="check" className="mr-2" />
              {isNewItem ? 'Create FAQ Item' : 'Save Source FAQ'}
            </span>
          )}
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div className="py-4 text-center" role="status" aria-live="polite">
            Loading FAQ...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div
            className="py-4 text-center text-red-500"
            role="alert"
            aria-live="polite"
          >
            Failed to load FAQ content. Please try again.
          </div>
        </div>
      </div>
    );
  }

  if (!hasReadPermission) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle id="faq-heading" />
                <ToolbarDescription>
                  Manage FAQ source content and translation workflow
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
          </Container>
        )}
        <div className="container-fixed">
          <div className="card card-grid min-w-full">
            <div className="card-body">
              <div className="py-4 text-center text-gray-500 dark:text-gray-700 contrast:text-white">
                You don&apos;t have permission to view FAQ content.
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="faq-heading" />
              <ToolbarDescription>
                Manage source FAQ items, then review translation drafts and
                publishing for each saved item.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {hasCreatePermission && (
                <button
                  className={clsx(
                    'btn btn-sm btn-light',
                    activeItemId === 'new' && 'btn-disabled opacity-50'
                  )}
                  onClick={handleCreateClick}
                  disabled={
                    activeItemId === 'new' || isMutationRunning || isFetching
                  }
                >
                  Add FAQ Item
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed space-y-6">
        {activeItemId === 'new' && (
          <div className="card card-grid min-w-full">
            <div className="card-header">
              <div>
                <h2 className="card-title">New FAQ Item</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-700 contrast:text-white/80">
                  Create the source content first, then open translation
                  workflow only when you are ready to review localized drafts.
                </p>
              </div>
            </div>
            <div className="card-body">{renderEditor(true)}</div>
          </div>
        )}

        {!items.length && activeItemId !== 'new' && (
          <div className="card card-grid min-w-full">
            <div className="card-body">
              <div className="py-4 text-center text-gray-500 dark:text-gray-700 contrast:text-white">
                No FAQ items added yet. Create the first source FAQ item to get
                started.
              </div>
            </div>
          </div>
        )}

        {items.map((item) => {
          const isEditingItem = activeItemId === item.id;

          return (
            <div key={item.id} className="card card-grid min-w-full">
              <div className="card-header flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="card-title">{item.title}</h2>
                    <span className="badge badge-success badge-sm">
                      Source: {getLanguageLabel(item.source_language)}
                    </span>
                    <span className="badge badge-outline">
                      Order {item.display_order}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {hasUpdatePermission && (
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => handleEditClick(item)}
                      disabled={isMutationRunning}
                    >
                      <KeenIcon icon="pencil" className="mr-2" />
                      Edit Source
                    </button>
                  )}
                  {hasDeletePermission && (
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => handleDelete(item)}
                      disabled={isMutationRunning}
                    >
                      <KeenIcon icon="trash" className="mr-2" />
                      Delete
                    </button>
                  )}
                </div>
              </div>

              <div className="card-body space-y-6">
                {isEditingItem && (
                  <Fragment>
                    {renderEditor(false)}
                    <CMSFAQTranslationWorkflowSection
                      item={item}
                      onRefresh={refetch}
                    />
                  </Fragment>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
};

export { FAQ };
